// Расчёт остатка аудио-минут из ответа GET /metrics-api/quota/{email}
// ({audio: {used, limit, action}}). limit=null = безлимит → null (текст не показываем).
export function quotaLeftMinutes(quota) {
  const a = quota && quota.audio;
  if (!a || typeof a.limit !== 'number') return null;
  return Math.max(0, Math.round(a.limit - (a.used || 0)));
}

// Порог предупреждения: меньше пятой части пакета — пора сказать это человеку,
// а не ждать, пока он упрётся в отказ при загрузке файла.
const LOW_SHARE = 0.2;
// Тревога: ниже десятой части. Тот же порог, по которому владельцу организации
// уходит письмо (bit-billing config.low_balance_share) — человек в интерфейсе и
// человек в почте должны видеть одно и то же событие, а не два разных.
//
// Уровень добавлен СЮДА, а не заведён второй шкалой рядом: quotaLevel читают и
// сайдбар, и баннер, и разойдись они — один и тот же остаток красился бы в двух
// местах по-разному.
const CRITICAL_SHARE = 0.1;

/** Уровень остатка: 'ok' | 'low' | 'critical' | 'empty'; null — лимита нет. */
export function quotaLevel(quota) {
  const a = quota && quota.audio;
  if (!a || typeof a.limit !== 'number' || a.limit <= 0) return null;
  const left = Math.max(0, a.limit - (a.used || 0));
  if (left <= 0) return 'empty';
  if (left < a.limit * CRITICAL_SHARE) return 'critical';
  return left < a.limit * LOW_SHARE ? 'low' : 'ok';
}

/**
 * Ограничивает ли лимит на самом деле.
 *
 * 🔴 У клиента и участника организации перерасход даёт отказ (429), у
 * сотрудника и партнёра — только запись в лог: bit-metrics возвращает им
 * log_warn (_determine_action, bit-metrics/app/main.py). Показывать таким
 * людям «осталось 0 мин», красную полосу и «Останется 0 мин» перед запуском
 * значит пугать пустотой, которой нет: работа не встанет.
 *
 * 21.09.2026 так испугались сотрудники, перешагнувшие 2000 минут за 30 дней:
 * двадцать три человека читали мягкий ориентир как кошелёк. Тот же урок уже
 * был выучен 16.08.2026 для баннера (см. QuotaBanner) — но гейт поставили
 * только там, а сайдбар, личный кабинет и экран запуска остались.
 */
export function quotaEnforced(quota) {
  if (!quota) return false;
  return quota.role === 'client' || !!quota.org_id;
}

/** Уровни, при которых остаток пора показать человеку. */
export const ALARM_LEVELS = ['low', 'critical', 'empty'];

/** true, если остаток пора показать: уровень тревожный. */
export function isAlarming(level) {
  return ALARM_LEVELS.includes(level);
}

// «Мало» на балансе — пятая часть минимального пополнения (MIN_TOPUP_RUB=500
// в bit-billing/app/balance.py), тот же принцип, что LOW_SHARE у минут.
const LOW_BALANCE_RUB = 100;

/**
 * Уровень остатка для «Оплаты по факту» — считаем по деньгам, а не по
 * audio.limit/used: у этой ветки audio.limit — это подарок (пакет минут в
 * месяц), used же копится за весь месяц и почти всегда его превышает —
 * дальше человек платит с баланса. quotaLevel() в этом случае красит строку
 * в красный оплатившему клиенту с деньгами на счету, будто всё кончилось.
 * null — не PAYG-клиент (balance_rub в ответе не пришёл).
 */
export function quotaLevelByBalance(quota) {
  if (!quota || quota.balance_rub === undefined || quota.balance_rub === null) return null;
  if (quota.audio && quota.audio.action === 'block') return 'empty';
  const giftLeft = quota.gift_minutes_left || 0;
  if (giftLeft <= 0 && quota.balance_rub < LOW_BALANCE_RUB) return 'low';
  return 'ok';
}

// Отказ по квоте приходит 429-м из shared/quota.py с текстом «Обратитесь к
// администратору». Внешнему клиенту администратор недоступен — подменяем текст
// своим, с дорогой к тарифам. Править сам shared нельзя без пересборки шести
// GPU-сервисов, поэтому подмена живёт здесь.
//
// У «Оплаты по факту» есть следующий шаг — пополнить; у остальных нет.
export function quotaBlockedMessage(error, quota) {
  if (error && error.response && error.response.status === 429) {
    // Если в ответе квоты есть balance_rub — это клиент на тарифе «Оплата по факту»
    if (quota && quota.balance_rub !== undefined && quota.balance_rub !== null) {
      return 'Баланс исчерпан. Пополните, чтобы продолжить';
    }
    return 'Пакет минут исчерпан';
  }
  return null;
}
